import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Table, Icon, Badge, Card, Dropdown, Menu, Row, Col } from 'antd';
import Confirm from '../Confirm';

const tableStyles = `
@keyframes pl-beat   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.22)} }
@keyframes pl-glow   { 0%,100%{box-shadow:0 0 0 0 var(--gc,rgba(24,144,255,.35))} 50%{box-shadow:0 0 0 8px rgba(0,0,0,0)} }
@keyframes pl-spin   { 0%{transform:rotate(0)} 100%{transform:rotate(180deg)} }
@keyframes pl-pulse  { 0%,100%{opacity:1} 50%{opacity:.45} }

/* ── Stat Cards ── */
.pl-stat-card { border-radius:10px!important; box-shadow:0 2px 12px rgba(24,144,255,0.08)!important; border:1px solid #e6f0ff!important; background:#fff!important; }
.pl-stat-icon { width:46px; height:46px; border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; animation:pl-glow 2.4s ease-in-out infinite; }

/* ── Row stripe ── */
.pl-row-stripe td { background:#f7f9ff!important; }

/* ── Card header ── */
.pl-card-header { padding:14px 20px 12px; border-bottom:1px solid #f0f5ff; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

/* ── Table overrides ── */
.pl-table .ant-table-thead > tr > th { background:#f0f7ff!important; font-size:11px!important; font-weight:700!important; color:#1890ff!important; letter-spacing:1px!important; text-transform:uppercase!important; border-bottom:2px solid #d6e8ff!important; white-space:nowrap; }
.pl-table .ant-table-tbody > tr > td { font-size:12px!important; color:#262626!important; font-weight:500!important; border-color:#e6f0ff!important; padding:8px 12px!important; }
.pl-table .ant-table-bordered .ant-table-thead > tr > th,
.pl-table .ant-table-bordered .ant-table-tbody > tr > td { border-color:#e6f0ff!important; }
.pl-table .ant-table-tbody > tr:hover > td { background:#e6f7ff!important; }
.pl-table .ant-spin-dot-item { background:#1890ff!important; }
.pl-table .ant-pagination-item-active { border-color:#1890ff!important; background:#1890ff!important; }
.pl-table .ant-pagination-item-active a { color:#fff!important; }
.pl-table .ant-pagination { padding:8px 16px!important; }

/* ── Status badges ── */
.pl-badge { display:inline-flex; align-items:center; gap:4px; padding:2px 9px; border-radius:10px; font-size:11px; font-weight:700; white-space:nowrap; }
.pl-dot   { width:6px; height:6px; border-radius:50%; flex-shrink:0; }
.pl-pending  { background:#fff7e6; border:1px solid #ffd591; color:#d46b08; }
.pl-pending  .pl-dot { background:#fa8c16; box-shadow:0 0 5px #fa8c16; animation:pl-pulse 1.4s ease-in-out infinite; }
.pl-approved { background:#f6ffed; border:1px solid #b7eb8f; color:#389e0d; }
.pl-approved .pl-dot { background:#52c41a; box-shadow:0 0 5px #52c41a; }
.pl-rejected { background:#fff1f0; border:1px solid #ffa39e; color:#cf1322; }
.pl-rejected .pl-dot { background:#ff4d4f; box-shadow:0 0 5px #ff4d4f; }

/* ── Ref chips ── */
.pl-ref { display:inline-flex; align-items:center; gap:4px; padding:2px 8px; border-radius:6px; font-size:11px; font-weight:700; white-space:nowrap; }
.pl-ref-spo { background:#f0f7ff; border:1px solid #d6e8ff; color:#0050b3; }
.pl-ref-po  { background:#f9f0ff; border:1px solid #d3adf7; color:#531dab; }
.pl-ref-jo  { background:#e6fffb; border:1px solid #87e8de; color:#08979c; }
.pl-ref-pr  { background:#fff7e6; border:1px solid #ffd591; color:#d46b08; }

/* ── Supplier ── */
.pl-supplier { font-size:12px; font-weight:600; color:#262626; }

/* ── Date ── */
.pl-date-wrap { display:flex; flex-direction:column; align-items:center; gap:2px; }
.pl-date-main { display:inline-flex; align-items:center; gap:4px; padding:2px 8px; border-radius:6px; background:#f9f0ff; border:1px solid #d3adf7; font-size:11px; font-weight:700; color:#531dab; }
.pl-date-rel  { font-size:10px; color:#8c8c8c; font-weight:500; font-style:italic; }

/* ── Gear button ── */
.pl-settings-btn { width:28px!important; height:28px!important; min-width:28px!important; padding:0!important; border-radius:7px!important; display:inline-flex!important; align-items:center!important; justify-content:center!important; font-size:13px!important; border:1.5px solid #d6e8ff!important; background:#e6f7ff!important; color:#1890ff!important; transition:all .18s!important; }
.pl-settings-btn.open { background:#1890ff!important; color:#fff!important; border-color:#1890ff!important; box-shadow:0 3px 10px rgba(24,144,255,.35)!important; }
.pl-settings-btn.open .anticon-setting { animation:pl-spin .4s cubic-bezier(.22,1,.36,1) forwards!important; }

/* ── Dropdown ── */
.pl-action-menu.ant-dropdown-menu { border-radius:10px!important; border:1px solid #e6f0ff!important; box-shadow:0 8px 28px rgba(24,144,255,.12)!important; padding:5px!important; min-width:170px!important; }
.pl-action-menu .ant-dropdown-menu-item { border-radius:7px!important; padding:8px 12px!important; font-size:12px!important; font-weight:600!important; transition:background .15s,transform .12s!important; }
.pl-action-menu .ant-dropdown-menu-item:hover { transform:translateX(3px)!important; background:#e6f7ff!important; }
.pl-action-menu .ant-dropdown-menu-item-danger:hover { background:#fff1f0!important; color:#ff4d4f!important; }
.pl-action-menu .ant-dropdown-menu-divider { background:#f0f5ff!important; margin:4px 0!important; }
.pl-remarks-dot .ant-badge-dot { background:#fa8c16!important; box-shadow:0 0 5px #fa8c16!important; }
`;

/* ── Stat Card ── */
const StatCard = ({ icon, label, value, color, bg, gc }) => (
  <Card className="pl-stat-card" bordered={false} bodyStyle={{ padding: '18px 20px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div className="pl-stat-icon" style={{ background: bg, '--gc': gc }}>
        <Icon type={icon} style={{ fontSize: 20, color }} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 500, letterSpacing: '.3px', whiteSpace: 'nowrap' }}>
          {label.toUpperCase()}
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
      </div>
    </div>
  </Card>
);

/* ── Status Badge ── */
const StatusBadge = ({ status }) => {
  const map = {
    PENDING:  { cls: 'pl-pending',  label: 'PENDING'  },
    APPROVED: { cls: 'pl-approved', label: 'APPROVED' },
    REJECTED: { cls: 'pl-rejected', label: 'REJECTED' },
  };
  const cfg = map[status] || { cls: 'pl-pending', label: status || '—' };
  return (
    <span className={`pl-badge ${cfg.cls}`}>
      <span className="pl-dot" />{cfg.label}
    </span>
  );
};

/* ── Action Cell ── */
const ActionCell = ({ data, openModal, approveOrReject, setLoading }) => {
  const [open, setOpen] = useState(false);
  const isPending  = data.status === 'PENDING';
  const hasRemarks = Boolean(data.remarks);

  const menu = (
    <Menu className="pl-action-menu">
      <Menu.Item key="view" onClick={() => openModal(data, 'pr')}>
        <Icon type="profile" style={{ marginRight: 8, color: '#1890ff' }} />
        View PO Details
      </Menu.Item>
      <Menu.Item
        key="remarks"
        onClick={() => openModal({ id: data.id, remarks: data.remarks || '' }, 'remarks')}
      >
        <Badge dot={hasRemarks} className="pl-remarks-dot">
          <Icon type="message" style={{ marginRight: 8, color: '#fa8c16' }} />
          {hasRemarks ? 'Edit Remarks' : 'Add Remarks'}
        </Badge>
      </Menu.Item>
      {isPending && <Menu.Divider />}
      {isPending && (
        <Menu.Item
          key="approve"
          onClick={() => Confirm(approveOrReject, null, 'approved', data.id, setLoading)}
        >
          <Icon type="check-circle" style={{ marginRight: 8, color: '#52c41a' }} />
          Approve Request
        </Menu.Item>
      )}
      {isPending && (
        <Menu.Item
          key="reject"
          className="ant-dropdown-menu-item-danger"
          onClick={() => Confirm(approveOrReject, null, 'rejected', data.id, setLoading)}
        >
          <Icon type="close-circle" style={{ marginRight: 8, color: '#ff4d4f' }} />
          Reject Request
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={['hover']}
      placement="bottomLeft"
      getPopupContainer={() => document.body}
      onVisibleChange={v => setOpen(v)}
    >
      <Button
        className={`pl-settings-btn${open ? ' open' : ''}`}
        size="small"
        icon="setting"
      />
    </Dropdown>
  );
};

/* ══ Main Component ══ */
const PendingListTable = props => {
  const {
    poList = [],
    openModal,
    approveOrReject,
    setLoading,
    paginate,
    onPageChange,
    onSizeChange,
    recordLength = 0,
  } = props;

  const safeList = Array.isArray(poList) ? poList : [];

  const total    = safeList.length;
  const pending  = safeList.filter(d => d.status === 'PENDING').length;
  const approved = safeList.filter(d => d.status === 'APPROVED').length;
  const rejected = safeList.filter(d => d.status === 'REJECTED').length;

  const statCards = [
    { icon: 'team',         label: 'Total',    value: total,    color: '#1890ff', bg: '#e6f7ff', gc: 'rgba(24,144,255,0.3)'  },
    { icon: 'clock-circle', label: 'Pending',  value: pending,  color: '#fa8c16', bg: '#fff7e6', gc: 'rgba(250,140,22,0.3)'  },
    { icon: 'check-circle', label: 'Approved', value: approved, color: '#52c41a', bg: '#f6ffed', gc: 'rgba(82,196,26,0.3)'   },
    { icon: 'close-circle', label: 'Rejected', value: rejected, color: '#ff4d4f', bg: '#fff1f0', gc: 'rgba(255,77,79,0.3)'   },
  ];

  const columns = [
    {
      key: 'actions',
      align: 'center',
      width: 46,
      render: record => (
        <ActionCell
          data={record}
          openModal={openModal}
          approveOrReject={approveOrReject}
          setLoading={setLoading}
        />
      ),
    },
    {
      key: 'status',
      title: 'Status',
      align: 'center',
      width: 120,
      render: record => <StatusBadge status={record.status} />,
      filters: [
        { text: 'PENDING',  value: 'PENDING'  },
        { text: 'APPROVED', value: 'APPROVED' },
        { text: 'REJECTED', value: 'REJECTED' },
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
    },
    {
      key: 'supplier',
      title: 'Supplier',
      dataIndex: 'supplier',
      align: 'center',
      render: val => <span className="pl-supplier">{val || '—'}</span>,
      sorter: (a, b) => (a.supplier || '').localeCompare(b.supplier || ''),
    },
    {
      key: 'spoNum',
      title: 'Purchase Order',
      dataIndex: 'spoNum',
      align: 'center',
      render: val => val
        ? <span className="pl-ref pl-ref-spo"><Icon type="file-done" style={{ fontSize: 10 }} /> {val}</span>
        : <Icon type="minus" style={{ color: '#d9d9d9' }} />,
    },
    {
      key: 'poNum',
      title: "Customer's PO",
      dataIndex: 'poNum',
      align: 'center',
      render: val => val
        ? <span className="pl-ref pl-ref-po"><Icon type="shopping" style={{ fontSize: 10 }} /> {val}</span>
        : <Icon type="minus" style={{ color: '#d9d9d9' }} />,
    },
    {
      key: 'joNum',
      title: 'Job Order',
      dataIndex: 'joNum',
      align: 'center',
      render: val => val
        ? <span className="pl-ref pl-ref-jo"><Icon type="tool" style={{ fontSize: 10 }} /> {val}</span>
        : <Icon type="minus" style={{ color: '#d9d9d9' }} />,
    },
    {
      key: 'prNum',
      title: 'Purchase Requisition',
      dataIndex: 'prNum',
      align: 'center',
      render: val => val
        ? <span className="pl-ref pl-ref-pr"><Icon type="form" style={{ fontSize: 10 }} /> {val}</span>
        : <Icon type="minus" style={{ color: '#d9d9d9' }} />,
    },
    {
      key: 'date',
      title: 'Date Requested',
      align: 'center',
      render: record => {
        if (!record.created_at) return <Icon type="minus" style={{ color: '#d9d9d9' }} />;
        return (
          <div className="pl-date-wrap">
            <span className="pl-date-main">
              <Icon type="calendar" style={{ fontSize: 10 }} />
              {moment(record.created_at).format('YYYY-MM-DD')}
            </span>
            <span className="pl-date-rel">{moment(record.created_at).fromNow()}</span>
          </div>
        );
      },
      sorter: (a, b) => moment(a.created_at || 0).unix() - moment(b.created_at || 0).unix(),
    },
  ];

  return (
    <>
      <style>{tableStyles}</style>

      {/* ══ STAT CARDS ══ */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {statCards.map((card, i) => (
          <Col key={i} xs={12} sm={6}>
            <StatCard {...card} />
          </Col>
        ))}
      </Row>

      {/* ══ TABLE CARD ══ */}
      <Card
        bordered={false}
        bodyStyle={{ padding: 0 }}
        style={{
          borderRadius: 10,
          boxShadow: '0 2px 12px rgba(24,144,255,0.07)',
          border: '1px solid #e6f0ff',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div className="pl-card-header">
          <Icon
            type="clock-circle"
            style={{ color: '#fa8c16', fontSize: 16, animation: 'pl-beat 1.8s ease-in-out infinite' }}
          />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#262626', letterSpacing: '.5px' }}>
            PENDING REQUESTS
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 20, height: 20, borderRadius: 10, padding: '0 6px',
            background: '#1890ff', color: '#fff', fontSize: 11, fontWeight: 600,
          }}>
            {recordLength || 0}
          </span>

          {/* Summary pills */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pending  > 0 && <span className="pl-badge pl-pending"><span className="pl-dot" />{pending} PENDING</span>}
            {approved > 0 && <span className="pl-badge pl-approved"><span className="pl-dot" />{approved} APPROVED</span>}
            {rejected > 0 && <span className="pl-badge pl-rejected"><span className="pl-dot" />{rejected} REJECTED</span>}
          </div>
        </div>

        {/* ── Table ── */}
        <Table
          className="pl-table"
          size="small"
          columns={columns}
          dataSource={safeList}
          rowKey={record => record.id || Math.random()}
          bordered
          style={{ overflowX: 'auto', margin: 0 }}
          rowClassName={(_, idx) => (idx % 2 === 0 ? '' : 'pl-row-stripe')}
          pagination={{
            total: recordLength,
            current: paginate ? paginate.page : 1,
            pageSize: paginate ? paginate.pageSize : 10,
            defaultPageSize: paginate ? paginate.pageSize : 10,
            showSizeChanger: true,
            onChange: onPageChange,
            onShowSizeChange: onSizeChange,
            pageSizeOptions: ['10', '15', '25'],
            showTotal: (tot, range) => `Showing ${range[0]}-${range[1]} of ${tot} items`,
            position: 'both',
            style: { padding: '8px 16px', marginBottom: 0 },
          }}
        />
      </Card>
    </>
  );
};

PendingListTable.propTypes = {
  poList:          PropTypes.arrayOf(PropTypes.object),
  openModal:       PropTypes.func.isRequired,
  approveOrReject: PropTypes.func.isRequired,
  setLoading:      PropTypes.func.isRequired,
  recordLength:    PropTypes.number,
  onSizeChange:    PropTypes.func.isRequired,
  onPageChange:    PropTypes.func.isRequired,
  paginate:        PropTypes.objectOf(PropTypes.any).isRequired,
};

PendingListTable.defaultProps = {
  poList:       [],
  recordLength: 0,
};

export default PendingListTable;